import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { on as wsOn } from "@/lib/socket";
import { formatMoney } from "@/lib/format";
import { Link } from "react-router-dom";
import { ArrowRight, Activity, AlertTriangle, CheckCircle2, Flame } from "lucide-react";

function Kpi({ label, value, sub, Icon, tone = "default", testId }) {
    const tones = {
        default: "border-border",
        good: "border-emerald-500/30",
        bad: "border-red-500/30",
        warn: "border-amber-400/30",
    };
    return (
        <div
            data-testid={testId}
            className={`p-5 bg-card border ${tones[tone]} rounded-md flex items-start justify-between`}
        >
            <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2 font-mono">
                    {label}
                </div>
                <div className="text-3xl font-semibold tracking-tight">{value}</div>
                {sub && (
                    <div className="text-xs text-muted-foreground mt-1">{sub}</div>
                )}
            </div>
            {Icon && <Icon className="size-4 text-muted-foreground" />}
        </div>
    );
}

export default function Dashboard() {
    const { user } = useAuth();
    const isAdmin = user?.role === "ADMIN";
    const [kpi, setKpi] = useState(null);
    const [recent, setRecent] = useState([]);

    const load = () => {
        api.get("/analytics/kpi").then((r) => setKpi(r.data)).catch(() => {});
        api.get("/tickets").then((r) => setRecent(r.data.slice(0, 10))).catch(() => {});
    };

    useEffect(() => {
        load();
        const off1 = wsOn("ticket:created", load);
        const off2 = wsOn("ticket:updated", load);
        const t = setInterval(load, 15000);
        return () => {
            off1();
            off2();
            clearInterval(t);
        };
    }, []);

    return (
        <div className="space-y-6" data-testid="dashboard-page">
            <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-primary font-mono">
                    Operations
                </div>
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
                    Support desk — live.
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                    {isAdmin
                        ? "Company-wide ticket metrics across all agents."
                        : "Metrics for tickets assigned to you."}
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Kpi testId="kpi-tickets-today" label="Tickets today" value={kpi?.total_today ?? "—"} Icon={Activity} />
                <Kpi testId="kpi-tickets-week" label="Last 7 days" value={kpi?.total_week ?? "—"} />
                <Kpi testId="kpi-tickets-month" label="Last 30 days" value={kpi?.total_month ?? "—"} />
                <Kpi testId="kpi-tickets-all" label="All time" value={kpi?.total_all ?? "—"} />
                <Kpi testId="kpi-open" label="Open" tone="warn" value={kpi?.open_count ?? "—"} Icon={AlertTriangle} />
                <Kpi testId="kpi-closed" label="Closed" tone="good" value={kpi?.closed_count ?? "—"} Icon={CheckCircle2} />
                <Kpi testId="kpi-escalated" label="Escalated" tone="bad" value={kpi?.escalated_count ?? "—"} Icon={Flame} />
                <Kpi testId="kpi-revenue-month" label="Paid / 30d" value={formatMoney(kpi?.paid_revenue_month ?? 0)} sub={`Today: ${formatMoney(kpi?.paid_revenue_today ?? 0)}`} />
            </div>

            <div className="p-5 bg-card border border-border rounded-md">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-lg font-semibold tracking-tight">Recent tickets</h2>
                    <Link
                        to="/tickets"
                        data-testid="dashboard-view-all-tickets"
                        className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                    >
                        View all <ArrowRight className="size-3" />
                    </Link>
                </div>
                <div className="divide-y divide-border">
                    {recent.length === 0 && (
                        <div className="text-sm text-muted-foreground py-6 text-center">
                            No tickets yet. Try the WhatsApp simulator to create one.
                        </div>
                    )}
                    {recent.map((t) => (
                        <Link
                            key={t.id}
                            to={`/tickets/${t.id}`}
                            data-testid={`dashboard-ticket-${t.ticket_number}`}
                            className="flex items-center gap-4 py-2 hover:bg-secondary/60 transition-colors px-2 rounded"
                        >
                            <span className="font-mono text-xs text-primary w-20">{t.ticket_number}</span>
                            <span className="text-sm truncate flex-1">{t.customer_name || t.customer_phone}</span>
                            <span className="text-xs text-muted-foreground w-40 truncate hidden md:block">{t.category_name || "—"}</span>
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">{t.priority_tier}</span>
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">{t.status}</span>
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
}
